import React, { useCallback } from "react";
import { ErrorMessage, QueryLoader, SlideoutDrawerV2 } from "@jsq/ds/core";
import { ObjectType } from "#graphql/supergraphEnums";
import { useGetPaymentBatchDetailQuery } from "#graphql/supergraphHooks";
import { usePaymentsContext } from "../Context/PaymentsContext";
import { PayablePaymentDetailsDisplay } from "./PayablePaymentDetailsDisplay";
import { PaymentDetailsDisplay } from "./PaymentDetailsDisplay";
import { errorMessageStyle, slideoutBody } from "./styles_vanilla.css";
import { PaymentBatch } from "./types";
import {
  mapPaymentBatchToPayablePaymentBatchDetails,
  mapPaymentBatchToPaymentBatchDetails as mapPaymentBatchToDistributionPaymentBatchDetails,
  getContainerStyleFromPaymentBatch,
} from "./utils";

type SlideoutInnerContentProps = {
  paymentBatch?: PaymentBatch;
  userId: number;
  isGPX: boolean;
  hasApprovePaymentPermission: boolean;
  totalPositions?: number;
};

const BATCH_OBJECT_TYPES: Set<string> = new Set([
  ObjectType.DistributionBatch,
  ObjectType.CapitalCallBatch,
  ObjectType.TransactionBatch,
]);

const SlideoutInnerContent: React.FC<SlideoutInnerContentProps> = ({
  paymentBatch,
  userId,
  isGPX,
  hasApprovePaymentPermission,
  totalPositions,
}) => {
  if (!paymentBatch || !paymentBatch.objectType) {
    return null;
  }

  if (BATCH_OBJECT_TYPES.has(paymentBatch.objectType)) {
    const paymentBatchDetails = mapPaymentBatchToDistributionPaymentBatchDetails(
      paymentBatch,
      userId,
      hasApprovePaymentPermission,
      isGPX,
      totalPositions,
    );

    return <PaymentDetailsDisplay paymentBatchDetails={paymentBatchDetails} />;
  }

  if (paymentBatch.objectType === ObjectType.Payable) {
    const payablePaymentBatchDetails = mapPaymentBatchToPayablePaymentBatchDetails(
      paymentBatch,
      userId,
      hasApprovePaymentPermission,
      isGPX,
    );

    return (
      <PayablePaymentDetailsDisplay payablePaymentBatchDetails={payablePaymentBatchDetails} />
    );
  }

  return null;
};

export type PaymentDetailsProps = {
  paymentBatchId?: string;
  onClose: () => void;
  totalPositions?: number;
};

/**
 * The payment batch details slideout drawer.
 */
export const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  paymentBatchId,
  onClose,
  totalPositions,
}: PaymentDetailsProps) => {
  const { isGPX, currentUserId, hasApprovePaymentPermission } = usePaymentsContext();

  const result = useGetPaymentBatchDetailQuery({
    variables: {
      id: paymentBatchId ?? "",
      isGPX,
    },
    skip: !paymentBatchId,
  });

  const paymentBatch = result.data?.paymentBatch;

  const renderError = useCallback(
    () => (
      <div className={errorMessageStyle}>
        <ErrorMessage>Content failed to load</ErrorMessage>
      </div>
    ),
    [],
  );

  return (
    <SlideoutDrawerV2
      // @ts-expect-error - OverlayStyle enum properly exported but auto-formatter removes import
      overlayStyle="transparent"
      isOpen={!!paymentBatchId}
      onClose={onClose}
      data-testid="payments-details-slideout"
    >
      <div className={getContainerStyleFromPaymentBatch(paymentBatch)}>
        <div className={slideoutBody}>
          <QueryLoader result={result} renderError={renderError}>
            <SlideoutInnerContent
              paymentBatch={paymentBatch}
              userId={currentUserId}
              isGPX={isGPX}
              hasApprovePaymentPermission={hasApprovePaymentPermission}
              totalPositions={totalPositions}
            />
          </QueryLoader>
        </div>
      </div>
    </SlideoutDrawerV2>
  );
};
